/**
 * Zustell-Kennzeichnung einer versendeten Nachricht ("Ist das angekommen?").
 * Gemeinsam genutzt vom Chat-Verlauf und vom klassischen Verlauf
 * (MessageThread, schwebendes Fenster), damit beide dasselbe anzeigen.
 */
import type { MessageDeliverySummary } from '@/services/deliveryApi'
import { isDeliveryProblem } from '@/services/deliveryStatus'

export type DeliveryTone = 'ok' | 'pending' | 'error'

/** Fachlicher Zustand hinter der Tönung: "gesendet ohne Bestätigung" ist auch grün, aber nicht "zugestellt". */
export type DeliveryKind = 'delivered' | 'accepted' | 'pending' | 'error'

export interface DeliveryBadge {
  tone: DeliveryTone
  kind: DeliveryKind
  label: string
  detail: string
  /** Zeitpunkt passend zum Status (zugestellt, gesendet bzw. gescheitert). */
  at?: string
}

/** Null für rein interne Nachrichten – dort gibt es nichts zuzustellen. */
export function deliveryBadge (
  d: Partial<MessageDeliverySummary> | null | undefined,
  t: (source: string) => string,
): DeliveryBadge | null {
  if (!d || !d.total) {
    return null
  }

  if (isDeliveryProblem(d.status)) {
    const who = (d.failingRecipients || []).join(', ')
    const failed = Number(d.failed || 0)
    return {
      tone: 'error',
      kind: 'error',
      label: failed > 1
        ? t('Zustellung an {n} Empfänger fehlgeschlagen').replace('{n}', String(failed))
        : t('Nicht zugestellt'),
      detail: [who, d.reason].filter(Boolean).join(' — '),
      at: d.failedAt,
    }
  }
  if (Number(d.pending || 0) > 0) {
    return { tone: 'pending', kind: 'pending', label: t('Zustellung läuft'), detail: '', at: d.sentAt }
  }
  const delivered = Number(d.delivered || 0)
  if (delivered > 0) {
    return {
      tone: 'ok',
      kind: 'delivered',
      label: delivered > 1
        ? t('An {n} Empfänger zugestellt').replace('{n}', String(delivered))
        : t('Zugestellt'),
      detail: '',
      at: d.deliveredAt,
    }
  }
  // Vom Mailserver angenommen, aber der Versandweg (SMTP, Postmark, Testmodus)
  // liefert keine Zustellbestätigung — nicht "läuft", sondern "gesendet".
  if (Number(d.accepted || 0) > 0) {
    return {
      tone: 'ok',
      kind: 'accepted',
      label: t('Gesendet'),
      detail: t('Vom Mailserver angenommen – für diesen Versandweg gibt es keine Zustellbestätigung.'),
      at: d.sentAt,
    }
  }
  return null
}

/** Nur bei "Zustellung läuft" lohnt ein manueller Abgleich mit dem Versanddienst. */
export function canRefreshDelivery (badge: DeliveryBadge | null | undefined): boolean {
  return badge?.tone === 'pending'
}
